import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import {
  blankTemplateUrl,
  buildMemeImageUrl,
  downloadMemeImage,
  fetchMemeTemplates,
  initialCaptions,
  lineLabel,
  templateFormat,
} from '../lib/memeGenApi'
import './meme-generator.css'

const SAMPLE_CAPTIONS = {
  drake: ['Writing docs', 'Shipping memes'],
  db: ['My responsibilities', 'A new side project', 'Me'],
  fine: ['Everything is fine', 'This is fine'],
  doge: ['Much wow', 'Very meme'],
}

function randomCaptionPair() {
  const tops = ['When the collider', 'Me explaining memes', 'POV: you found MDMC']
  const bottoms = ['Hits just right', 'To my coworkers', 'At 2am on a Tuesday']
  return [
    tops[Math.floor(Math.random() * tops.length)],
    bottoms[Math.floor(Math.random() * bottoms.length)],
  ]
}

const MemeGenerator = () => {
  const [templates, setTemplates] = useState([])
  const [loadError, setLoadError] = useState(null)
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [query, setQuery] = useState('')
  const [formatFilter, setFormatFilter] = useState('all')
  const [selectedId, setSelectedId] = useState('drake')
  const [captions, setCaptions] = useState(['', ''])
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [downloadBusy, setDownloadBusy] = useState(false)
  const [actionError, setActionError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoadingTemplates(true)
    setLoadError(null)
    fetchMemeTemplates()
      .then((list) => {
        if (cancelled) return
        setTemplates(list)
        if (list.length > 0 && !list.some((t) => t.id === 'drake')) {
          setSelectedId(list[0].id)
        }
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Could not load templates')
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  )

  useEffect(() => {
    if (!selected) return
    const sample = SAMPLE_CAPTIONS[selected.id]
    setCaptions(sample ?? initialCaptions(selected))
  }, [selected?.id])

  const filteredTemplates = useMemo(() => {
    const q = query.trim().toLowerCase()
    return templates.filter((t) => {
      const format = templateFormat(t)
      const matchesFormat = formatFilter === 'all' || formatFilter === format
      if (!matchesFormat) return false
      if (!q) return true
      return (
        t.id.toLowerCase().includes(q) ||
        (t.name || '').toLowerCase().includes(q) ||
        (t.keywords || []).some((k) => k.toLowerCase().includes(q))
      )
    })
  }, [templates, query, formatFilter])

  const hasCaptionText = useMemo(
    () => captions.some((c) => String(c).trim().length > 0),
    [captions]
  )

  useEffect(() => {
    if (!selected) {
      setPreviewUrl(null)
      return
    }

    if (!hasCaptionText) {
      setPreviewUrl(blankTemplateUrl(selected))
      setPreviewLoading(false)
      return
    }

    const timer = window.setTimeout(() => {
      try {
        setPreviewLoading(true)
        setActionError(null)
        const url = buildMemeImageUrl(selected.id, captions)
        setPreviewUrl(url)
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Preview failed')
        setPreviewUrl(blankTemplateUrl(selected))
      } finally {
        setPreviewLoading(false)
      }
    }, 400)

    return () => window.clearTimeout(timer)
  }, [selected, captions, hasCaptionText])

  const handleCaptionChange = useCallback((index, value) => {
    setCaptions((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const handleRandomize = useCallback(() => {
    if (!selected) return
    const count = Math.max(1, selected.lines ?? 1)
    if (count === 2) {
      const [top, bottom] = randomCaptionPair()
      setCaptions([top, bottom])
      return
    }
    if (selected.example?.text?.length >= count) {
      setCaptions(selected.example.text.slice(0, count))
      return
    }
    setCaptions(
      Array.from({ length: count }, (_, i) => `Caption ${i + 1} goes here`)
    )
  }, [selected])

  const handleDownload = useCallback(async () => {
    if (!selected || !previewUrl) return
    setDownloadBusy(true)
    setActionError(null)
    try {
      const slug = selected.id.replace(/[^a-z0-9_-]+/gi, '-')
      await downloadMemeImage(previewUrl, `mdmc-${slug}.jpg`)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setDownloadBusy(false)
    }
  }, [selected, previewUrl])

  const handleCopyLink = useCallback(async () => {
    if (!previewUrl) return
    try {
      await navigator.clipboard.writeText(previewUrl)
    } catch {
      setActionError('Could not copy link — try Download instead.')
    }
  }, [previewUrl])

  return (
    <div className="meme-generator-page">
      <Helmet>
        <title>Free Meme Generator — MDMC.dev</title>
        <meta
          name="description"
          content="Create classic meme images for free. Pick a template, add captions, download instantly — powered by MDMC."
        />
        <meta property="og:title" content="Free Meme Generator — MDMC.dev" />
        <meta
          property="og:description"
          content="Pick a template, type your captions, download your meme — no login required."
        />
        <link rel="canonical" href="https://mdmc.dev/meme-generator" />
      </Helmet>
      <Navigation />
      <main>
        <section className="mg-hero">
          <span className="mg-hero-badge">Free · No login · SFW</span>
          <h1 className="mg-hero-title">Meme Generator</h1>
          <p className="mg-hero-subtitle">
            Pick a classic template, write your captions, and download the image.
            This is the public-safe collider lane: no NSFW models, no private
            DogeForge controls, no login. Just dank, office-survivable output.
          </p>
        </section>

        <div className="mg-workspace">
          <div className="mg-controls">
            <div className="mg-panel">
              <p className="mg-panel-title">Templates</p>
              {loadingTemplates ? (
                <p className="mg-status loading">Loading templates…</p>
              ) : loadError ? (
                <p className="mg-status error">{loadError}</p>
              ) : (
                <>
                  <input
                    type="search"
                    className="mg-search"
                    placeholder="Search templates…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search meme templates"
                  />
                  <div className="mg-filter-row" aria-label="Template filters">
                    {[
                      ['all', 'All'],
                      ['classic', 'Classic'],
                      ['multi', 'Multi-box'],
                      ['single', 'Single'],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className={`mg-filter-chip${formatFilter === id ? ' is-active' : ''}`}
                        onClick={() => setFormatFilter(id)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="mg-template-grid" role="listbox" aria-label="Meme templates">
                    {filteredTemplates.slice(0, 120).map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        role="option"
                        aria-selected={t.id === selectedId}
                        className={`mg-template-card${t.id === selectedId ? ' is-selected' : ''}`}
                        onClick={() => setSelectedId(t.id)}
                      >
                        <img
                          src={blankTemplateUrl(t)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                        <span>{t.name || t.id}</span>
                      </button>
                    ))}
                  </div>
                  {filteredTemplates.length === 0 ? (
                    <p className="mg-status loading">No templates match your search.</p>
                  ) : null}
                </>
              )}
            </div>

            {selected ? (
              <div className="mg-panel" style={{ marginTop: 'var(--spacing-xl)' }}>
                <p className="mg-panel-title">Captions — {selected.name || selected.id}</p>
                {captions.map((value, index) => (
                  <div key={`${selected.id}-${index}`} className="mg-caption-field">
                    <label htmlFor={`caption-${index}`}>{lineLabel(selected, index)}</label>
                    <input
                      id={`caption-${index}`}
                      type="text"
                      value={value}
                      maxLength={120}
                      placeholder={`Text for ${lineLabel(selected, index).toLowerCase()}`}
                      onChange={(e) => handleCaptionChange(index, e.target.value)}
                    />
                  </div>
                ))}
                <div className="mg-actions">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleRandomize}>
                    Randomize
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mg-panel mg-preview-panel">
            <p className="mg-panel-title">Preview</p>
            <div className="mg-preview-wrap">
              {previewLoading ? (
                <span className="mg-preview-loading">Updating…</span>
              ) : null}
              {previewUrl ? (
                <img
                  key={previewUrl}
                  src={previewUrl}
                  alt={selected ? `${selected.name || selected.id} meme preview` : 'Meme preview'}
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => {
                    setPreviewLoading(false)
                    setActionError('Preview failed to load. Try simpler caption text.')
                  }}
                />
              ) : (
                <div className="mg-preview-empty">Select a template to begin.</div>
              )}
            </div>
            <div className="mg-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!previewUrl || downloadBusy}
                onClick={handleDownload}
              >
                {downloadBusy ? 'Saving…' : 'Download meme'}
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={!previewUrl || !hasCaptionText}
                onClick={handleCopyLink}
              >
                Copy image URL
              </button>
            </div>
            {actionError ? <p className="mg-status error">{actionError}</p> : null}
            <p className="mg-attribution">
              Images rendered via{' '}
              <a href="https://memegen.link/" target="_blank" rel="noopener noreferrer">
                memegen.link
              </a>
              . Want collisions and lineage?{' '}
              <a href="/collider">Enter the Collider</a>.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default MemeGenerator
